// Instrument Master — IndexedDB cache + search index
import angelOneApi from './angelOneApi';

const DB_NAME = 'AngelOneInstruments';
const DB_VERSION = 1;
const STORE_NAME = 'instruments';
const META_STORE = 'meta';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'token' });
        store.createIndex('symbol', 'symbol', { unique: false });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('exchange_symbol', ['exch_seg', 'symbol'], { unique: false });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
    };
  });
}

async function getMeta(db) {
  return new Promise((resolve) => {
    const tx = db.transaction(META_STORE, 'readonly');
    const store = tx.objectStore(META_STORE);
    const req = store.get('lastUpdated');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

async function setMeta(db, data) {
  const tx = db.transaction(META_STORE, 'readwrite');
  const store = tx.objectStore(META_STORE);
  store.put({ key: 'lastUpdated', ...data });
}

async function storeInstruments(db, instruments) {
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  store.clear();
  instruments.forEach(inst => store.put(inst));
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadInstrumentMaster(forceRefresh = false) {
  const db = await openDB();
  const meta = await getMeta(db);

  const today = new Date().toISOString().split('T')[0];
  const isStale = !meta || meta.date !== today;

  if (!forceRefresh && !isStale) {
    return { cached: true, date: meta.date };
  }

  try {
    const result = await angelOneApi.getInstrumentMaster();
    if (result.data?.status && result.data?.instruments) {
      await storeInstruments(db, result.data.instruments);
      await setMeta(db, { date: today, count: result.data.count });
      return { cached: false, date: today, count: result.data.count };
    }
  } catch (error) {
    console.error('Failed to load instrument master:', error);
    if (meta) return { cached: true, date: meta.date, error: error.message };
    throw error;
  }
}

export async function searchInstruments(query, exchange = null, limit = 20) {
  if (!query || query.length < 2) return [];

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const results = [];
  const upperQuery = query.toUpperCase();

  return new Promise((resolve) => {
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor && results.length < limit) {
        const inst = cursor.value;
        const matches = inst.symbol?.toUpperCase().includes(upperQuery) ||
                       inst.name?.toUpperCase().includes(upperQuery);
        const exchangeMatch = !exchange || inst.exch_seg === exchange;

        if (matches && exchangeMatch) {
          results.push(inst);
        }
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    cursorReq.onerror = () => resolve(results);
  });
}

export async function getInstrument(token) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  return new Promise((resolve) => {
    const req = store.get(token);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

export async function getInstrumentsByTokens(tokens) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const results = [];

  for (const token of tokens) {
    const inst = await new Promise((resolve) => {
      const req = store.get(token);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    });
    if (inst) results.push(inst);
  }

  return results;
}
